import { useEffect, useState } from 'react'
import { X } from 'lucide-react'
import { fieldLabel, fieldWrapper, selectBase, textareaBase } from '@/lib/form-styles'
import { apiErrorText } from '@/lib/api-error'
import { fetchLostReasons } from '../api/calculation-settings.api'
import { changeCalculationStatus } from '../api/calculations.api'
import type { CalcLostReason, Calculation } from '../types/calculation.types'

/**
 * AP-37 4.1 — **„nicht beauftragt"** mit Grund aus der Mandantenliste und
 * Freitext. Ein Dialog, der einen Vorgang als Einheit erfasst — Knopf mit dem
 * Verb (AP-36).
 */
export function CalculationStatusDialog({
  kalkulation,
  onClose,
  onDone,
}: {
  kalkulation: Calculation
  onClose: () => void
  onDone: (k: Calculation) => void
}) {
  const [gruende, setGruende] = useState<CalcLostReason[]>([])
  const [grund, setGrund] = useState('')
  const [notiz, setNotiz] = useState('')
  const [fehler, setFehler] = useState<string | null>(null)

  useEffect(() => {
    fetchLostReasons()
      .then((g) => setGruende(g.filter((x) => x.isActive)))
      .catch(() => setGruende([]))
  }, [])

  const setzen = async () => {
    try {
      onDone(await changeCalculationStatus(kalkulation.id, { status: 'LOST', lostReasonKey: grund, lostNote: notiz.trim() || undefined }))
    } catch (e) {
      setFehler(apiErrorText(e, 'Der Status ließ sich nicht ändern.'))
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4">
      <div className="w-full max-w-md rounded-lg border border-border p-6 shadow-xl" style={{ background: 'hsl(var(--card))', color: 'hsl(var(--card-foreground))' }}>
        <div className="mb-4 flex items-start justify-between">
          <h2 className="text-lg font-semibold text-primary">Nicht beauftragt</h2>
          <button type="button" onClick={onClose} className="text-muted-foreground hover:text-foreground" aria-label="Schließen">
            <X className="h-5 w-5" />
          </button>
        </div>
        {fehler && <div className="mb-4 rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">{fehler}</div>}
        <div className="space-y-4">
          <div className={fieldWrapper}>
            <label className={fieldLabel}>Grund</label>
            <select className={selectBase} value={grund} onChange={(e) => setGrund(e.target.value)}>
              <option value="">— bitte wählen —</option>
              {gruende.map((g) => (
                <option key={g.id} value={g.key}>
                  {g.name}
                </option>
              ))}
            </select>
          </div>
          <div className={fieldWrapper}>
            <label className={fieldLabel}>Anmerkung</label>
            <textarea className={textareaBase} rows={3} value={notiz} onChange={(e) => setNotiz(e.target.value)} placeholder="z. B. Mitbewerber 8 % günstiger" />
          </div>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" className="rounded-md border border-border px-4 py-2 text-sm" onClick={onClose}>
            Abbrechen
          </button>
          <button type="button" className="rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground disabled:opacity-50" disabled={!grund} onClick={() => void setzen()}>
            Als nicht beauftragt kennzeichnen
          </button>
        </div>
      </div>
    </div>
  )
}
